import type { DocumentCaptureStepValues } from '../constants';
import type { NextStep, DocumentOcrRetakePayload } from '@repo/common';

import { Step } from '@repo/common';
import { useCallback, useEffect, useState } from 'react';

import { DocumentCaptureStep } from '../constants';

import { useAppContext } from '@/providers/app-provider/context';
import { postSubmitHandled } from '@/services/rivaas-api';
import { applyBffSubmitResponse, buildSubmitErrorContext, retrySubmitErrorStep } from '@/utils/submit-error';
import type { mapBffErrorCodeToFailReason } from '@/utils/bff-error-mapping';
import { isStepSkippable } from '@/utils/workflow';

/** Steps where the UI shows the “second page” capture (IDV + OCR two-page flows). */
const BACK_STEPS = [Step.DOCUMENT_BACK, Step.DOCUMENT_OCR_BACK] as const;

export function useDocumentUpload(currentAppStep: NextStep) {
  const { dispatch, state } = useAppContext();
  const [captureStep, setCaptureStep] = useState<DocumentCaptureStepValues>(DocumentCaptureStep.FRONT_PAGE);
  const [captureRenderKey, setCaptureRenderKey] = useState(0);
  const [page, setPage] = useState<Blob | null>(null);
  const [serverErrorFailReason, setServerErrorFailReason] = useState<
    ReturnType<typeof mapBffErrorCodeToFailReason> | null
  >(null);

  const [serverErrorMessage, setServerErrorMessage] = useState<string | null>(null);
  const [serverErrorRetryable, setServerErrorRetryable] = useState(false);
  const [retakeNotice, setRetakeNotice] = useState<DocumentOcrRetakePayload | null>(null);

  const redirectOptions = {
    unverifiedUrl: state.customerData?.redirect?.unverifiedUrl,
    verifiedUrl: state.customerData?.redirect?.verifiedUrl,
  };

  const uploadDocument = async (blob: Blob, arrayBuffer: Uint8Array) => {
    setServerErrorFailReason(null);
    setServerErrorMessage(null);
    setServerErrorRetryable(false);
    setRetakeNotice(null);

    setPage(blob);
    setCaptureStep(DocumentCaptureStep.PROCESSING);

    const data = await postSubmitHandled(new Blob([new Uint8Array(arrayBuffer)], { type: 'image/jpeg' }));

    if (applyBffSubmitResponse(dispatch, data, redirectOptions)) {
      const errorContext = buildSubmitErrorContext(data);
      const isBackStep = BACK_STEPS.some((s) => s === currentAppStep.value);

      setPage(null);
      setCaptureStep(isBackStep ? DocumentCaptureStep.BACK_PAGE : DocumentCaptureStep.FRONT_PAGE);
      setServerErrorFailReason(errorContext?.failReason ?? null);
      setServerErrorMessage(errorContext?.message ?? null);
      setServerErrorRetryable(errorContext?.retryable ?? false);

      return data;
    }

    // OCR-quality retake rewinds to document_front without `error`.
    if (data.retake || data.nextStep.step === 'document_front') {
      setPage(null);
      setCaptureStep(DocumentCaptureStep.FRONT_PAGE);
      setCaptureRenderKey((value) => value + 1);

      if (data.retake) {
        setRetakeNotice(data.retake);
      } else {
        setRetakeNotice({
          reason_code: 'UNKNOWN',
          retake_count: 1,
          retake_limit: 1,
        });
      }
    }

    return data;
  };

  const continueToBackPage = () => {
    setCaptureStep(DocumentCaptureStep.BACK_PAGE);
  };

  useEffect(() => {
    if (BACK_STEPS.some((s) => s === currentAppStep.value)) {
      if (isStepSkippable(currentAppStep)) {
        setCaptureStep(DocumentCaptureStep.REVIEW);
      } else {
        setCaptureStep(DocumentCaptureStep.BACK_PAGE);
      }
    }
  }, [currentAppStep]);

  const clearServerError = useCallback(() => {
    setServerErrorFailReason(null);
    setServerErrorMessage(null);
    setServerErrorRetryable(false);
  }, []);

  const dismissRetakeNotice = useCallback(() => {
    setRetakeNotice(null);
  }, []);

  const retryCapture = useCallback(() => {
    clearServerError();
    setPage(null);
    setCaptureStep(
      BACK_STEPS.some((s) => s === currentAppStep.value)
        ? DocumentCaptureStep.BACK_PAGE
        : DocumentCaptureStep.FRONT_PAGE,
    );
    setCaptureRenderKey((value) => value + 1);
    retrySubmitErrorStep(dispatch, state.submitErrorContext, redirectOptions);
  }, [clearServerError, currentAppStep.value, dispatch, redirectOptions, state.submitErrorContext]);

  return {
    captureRenderKey,
    captureStep,
    clearServerError,
    continueToBackPage,
    dismissRetakeNotice,
    page,
    retakeNotice,
    retryCapture,
    serverErrorFailReason,
    serverErrorMessage,
    serverErrorRetryable,
    uploadDocument,
  };
}
